import { AnimatePresence } from 'motion/react';
import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { RequireBusiness } from '@/app/guards/RequireBusiness';
import { AppShell, BootSplash, PageTransition } from '@/components/layout';

const LandingPage = lazy(() => import('@/pages/LandingPage'));
const SetupPage = lazy(() => import('@/pages/SetupPage'));
const QuoteBuilderPage = lazy(() => import('@/pages/QuoteBuilderPage'));
const QuoteHistoryPage = lazy(() => import('@/pages/QuoteHistoryPage'));
const ServicesPage = lazy(() => import('@/pages/ServicesPage'));
const CustomersPage = lazy(() => import('@/pages/CustomersPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

/**
 * Every route is code-split, so mounting one suspends. The Suspense boundary
 * must live *below* `AnimatePresence`, not above it.
 *
 * With the boundary above, a lazy chunk suspending tears down the whole
 * presence subtree mid-transition. `mode="wait"` is holding the outgoing page
 * until its exit animation reports completion — a callback that never arrives,
 * because the tree it belonged to was suspended away. The incoming page then
 * never commits, and only a full reload (which mounts the route with no
 * outgoing child to wait for) recovers. That was the "setup page needs a
 * refresh" bug.
 *
 * Wrapping each element instead means the animated container mounts
 * immediately and the fallback renders inside it.
 */
function Lazy({ children }: { children: ReactNode }) {
  return (
    <PageTransition>
      <Suspense fallback={<BootSplash />}>{children}</Suspense>
    </PageTransition>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  // Animate top-level navigation only — switching tabs inside the app shell
  // should feel instant, not like a page load.
  const animationKey = location.pathname.startsWith('/app') ? 'app' : location.pathname;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={animationKey}>
        <Route
          path="/"
          element={
            <Lazy>
              <LandingPage />
            </Lazy>
          }
        />
        <Route
          path="/setup"
          element={
            <Lazy>
              <SetupPage />
            </Lazy>
          }
        />

        <Route element={<RequireBusiness />}>
          <Route path="/app" element={<AppShell />}>
            <Route index element={<Navigate to="/app/new" replace />} />
            <Route path="new" element={<Suspense fallback={<BootSplash />}><QuoteBuilderPage /></Suspense>} />
            <Route path="quotes" element={<Suspense fallback={<BootSplash />}><QuoteHistoryPage /></Suspense>} />
            <Route path="services" element={<Suspense fallback={<BootSplash />}><ServicesPage /></Suspense>} />
            <Route path="customers" element={<Suspense fallback={<BootSplash />}><CustomersPage /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={<BootSplash />}><SettingsPage /></Suspense>} />
          </Route>
        </Route>

        <Route
          path="*"
          element={
            <Lazy>
              <NotFoundPage />
            </Lazy>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <AnimatedRoutes />
    </BrowserRouter>
  );
}
